import { FileText, Layout, BarChart3, Target, TrendingUp, Activity, Zap } from 'lucide-react';
import KeywordLandingPage, {
  type ProblemCard,
  type FeatureCard,
} from '../components/KeywordLandingPage';

const problemCards: ProblemCard[] = [
  {
    title: 'The plan is difficult to use',
    description:
      'A long document can make it hard to find the priorities and actions that matter in the next decision.',
    icon: FileText,
    iconBgClass: 'bg-red-100',
    iconColorClass: 'text-red-600',
  },
  {
    title: 'Business knowledge needs structure',
    description:
      'Your understanding of customers, competition, and company strengths needs a clear place in the plan.',
    icon: Layout,
    iconBgClass: 'bg-orange-100',
    iconColorClass: 'text-orange-600',
  },
  {
    title: 'Actions sit outside the plan',
    description:
      'Projects and tasks are easier to prioritise when the objectives and strategies behind them stay visible.',
    icon: BarChart3,
    iconBgClass: 'bg-yellow-100',
    iconColorClass: 'text-yellow-600',
  },
];

const features: FeatureCard[] = [
  {
    title: 'A structured foundation',
    description: 'Capture your vision, mission, values, and measurable objectives.',
    icon: Target,
    gradientFrom: 'from-blue-50',
    gradientTo: 'to-blue-100',
    iconColorClass: 'text-blue-600',
  },
  {
    title: 'Market and business context',
    description: 'Save PESTEL, Five Forces, SWOT, success factors, and competitor profiles.',
    icon: Layout,
    gradientFrom: 'from-purple-50',
    gradientTo: 'to-purple-100',
    iconColorClass: 'text-purple-600',
  },
  {
    title: 'Objectives and strategic priorities',
    description: 'Choose strategies and connect them to the outcomes you want to pursue.',
    icon: TrendingUp,
    gradientFrom: 'from-green-50',
    gradientTo: 'to-green-100',
    iconColorClass: 'text-green-600',
  },
  {
    title: 'Practical delivery planning',
    description: 'Break strategies into projects, tasks, and subtasks with owners and dates.',
    icon: Activity,
    gradientFrom: 'from-orange-50',
    gradientTo: 'to-orange-100',
    iconColorClass: 'text-orange-600',
  },
  {
    title: 'AI drafting assistance',
    description:
      'Develop starting points for your foundation, strategies, and report descriptions.',
    icon: Zap,
    gradientFrom: 'from-red-50',
    gradientTo: 'to-red-100',
    iconColorClass: 'text-red-600',
  },
  {
    title: 'A presentable plan',
    description: 'Build custom visual reports and export your business strategy as a PDF.',
    icon: BarChart3,
    gradientFrom: 'from-indigo-50',
    gradientTo: 'to-indigo-100',
    iconColorClass: 'text-indigo-600',
  },
];

export default function BusinessPlanningSoftwarePage() {
  return (
    <KeywordLandingPage
      headline="Build a business plan you can put to work."
      subheadline="Set your direction, assess your business, and choose your priorities. Connect the plan to projects and tasks, then present it in a clear visual report."
      bullets={[
        'A guided foundation for your business',
        'Market and internal assessment frameworks',
        'Measurable objectives and strategies',
        'Connected delivery and PDF reports',
      ]}
      badgeText="Business Planning"
      problemTitle="A useful business plan makes the next decisions clearer."
      problemSubtitle="The value comes from bringing direction, business context, and practical next steps together as you decide what to do."
      problemCards={problemCards}
      solutionTitle="Build the plan around direction, decisions, and delivery."
      solutionSubtitle="StratPlan gives your business plan a guided structure and a visible connection to the work required to move it forward."
      solutionSteps={[
        {
          title: 'Set the direction',
          description:
            'Define your vision, mission, values, and objectives, with AI drafts available from the context you provide.',
        },
        {
          title: 'Choose the priorities',
          description:
            'Work through market and internal assessments, then develop strategies for the objectives that matter.',
        },
        {
          title: 'Organise and present the next steps',
          description:
            'Link projects and tasks to your strategies, assign owners, and create a visual plan to share as a PDF.',
        },
      ]}
      featuresTitle="Build a plan your business can use"
      features={features}
      finalCtaTitle="Give your business a clear plan for what comes next."
      finalCtaSubtitle="Create your account and connect your company direction to strategic priorities and practical work."
      finalCtaButtonText="Start my strategic plan"
      analyticsCategory="business_planning_landing"
      breadcrumbLabel="Business Planning"
    />
  );
}
